import React, { useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faRedoAlt,
  faSpinner,
  faUndoAlt,
  faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { Button, UncontrolledTooltip } from 'reactstrap';
import { toast } from 'react-toastify';
import { DepartmentContext } from '../../../../../DepartmentWrapper';
import { useAuth0 } from '../../../../../auth0/reactAuth0Spa';
import { putData } from '../../../../../Services/dataApi';
import { formatBearerToken, checkLoginForError } from '../../../../../Common.functions';
import { popoverModifiers } from '../../../../../enums';

const PostImportPreviewItem = (props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isFailedToRestore, setIsFailedToRestore] = useState(true);

  const dContext = useContext(DepartmentContext);

  const { getTokenSilently, loginWithRedirect } = useAuth0();

  const { item, type, uploadStatus, createCandidates, i, itemIndex, rowKey } = props;

  const messages = {
    deleted: 'Candidate was updated but is marked as deleted.',
    inserted: 'New Candidate added.',
    updated: 'Candidate was updated.',
    restored: 'Updated Candidate but initially marked as deleted. Now Restored.',
    orphaned: 'Unable to import - Missing Match Field',
    queued: 'Candidate is queued for upload.',
    uploading: 'Candidate is being uploaded.',
    failed: 'Candidate upload failed.',
    orphaned_conflict: 'Unable to import - Conflicting Match Fields',
  };

  const icons = {
    deleted: faUndoAlt,
    inserted: faCheckCircle,
    updated: faCheckCircle,
    orphaned: faExclamationCircle,
    orphaned_conflict: faExclamationCircle,
    queued: faClock,
    uploading: faSpinner,
    uploaded: faCheckCircle,
    failed: faRedoAlt,
  };

  const colors = {
    deleted: '#ffa459',
    inserted: '#CEFFDA ',
    updated: '#aeaefb',
    orphaned: '#ff8080',
    orphaned_conflict: '#ff8080',
    queued: '#D0D0D0',
    failed: '#ff8080',
  };

  const {
    FirstName,
    LastName,
    AAMCID,
    SFMatchID,
    Email,

    AAMCIDDuplicates = [],
    SFMatchIDDuplicates = [],
    EmailDuplicates = [],
    existingCandidate = {},
  } = item;

  const PhotoUrl = existingCandidate.PhotoUrl || null;

  const AAMCIDIconWithTooltip = (
    <>
      <FontAwesomeIcon
        id={`AAMCID_invalidIcon_${itemIndex}_${i}`}
        icon={faInfoCircle}
        style={{ cursor: 'pointer', marginLeft: 10 }}
      />
      <UncontrolledTooltip placement="top" target={`AAMCID_invalidIcon_${itemIndex}_${i}`} modifiers={popoverModifiers}>
        This candidate shares it's AAMCID value with {AAMCIDDuplicates.length} other candidate
        {AAMCIDDuplicates.length > 1 ? 's' : ''}:
        <br />
        {AAMCIDDuplicates.map((c, ii) => {
          if (ii == i) {
            return;
          }
          const { FirstName, LastName, AAMCID = '---', SFMatchID = '---', Email = '---' } = c;
          let tooltipName = `${LastName || '-----'}, ${FirstName || '-----'}`;

          return <div>{tooltipName}</div>;
        })}
      </UncontrolledTooltip>
    </>
  );

  const SFMatchIDIconWithTooltip = (
    <>
      <FontAwesomeIcon
        id={`SFMatchID_invalidIcon_${itemIndex}_${i}`}
        icon={faInfoCircle}
        style={{ cursor: 'pointer', marginLeft: 10 }}
      />
      <UncontrolledTooltip
        placement="top"
        target={`SFMatchID_invalidIcon_${itemIndex}_${i}`}
        modifiers={popoverModifiers}
      >
        This candidate shares it's SFMatchID value with {SFMatchIDDuplicates.length} other candidate
        {SFMatchIDDuplicates.length > 1 ? 's' : ''}:
        <br />
        {SFMatchIDDuplicates.map((c, ii) => {
          if (ii == i) {
            return;
          }
          const { FirstName, LastName, AAMCID = '---', SFMatchID = '---', Email = '---' } = c;
          let tooltipName = `${LastName || '-----'}, ${FirstName || '-----'}`;

          return <div>{tooltipName}</div>;
        })}
      </UncontrolledTooltip>
    </>
  );

  const EmailIconWithTooltip = (
    <>
      <FontAwesomeIcon
        id={`Emails_invalidIcon_${itemIndex}_${i}`}
        icon={faInfoCircle}
        style={{ cursor: 'pointer', marginLeft: 10 }}
      />
      <UncontrolledTooltip placement="top" target={`Emails_invalidIcon_${itemIndex}_${i}`} modifiers={popoverModifiers}>
        This candidate shares it's Email value with {EmailDuplicates.length} other candidate
        {EmailDuplicates.length > 1 ? 's' : ''}:
        <br />
        {EmailDuplicates.map((c, ii) => {
          if (ii == i) {
            return;
          }
          const { FirstName, LastName, AAMCID = '---', SFMatchID = '---', Email = '---' } = c;
          let tooltipName = `${LastName || '-----'}, ${FirstName || '-----'}`;

          return <div>{tooltipName}</div>;
        })}
      </UncontrolledTooltip>
    </>
  );
  // Try to find a key that exists in uploadStatus, in priority order
  const findBestKey = () => {
    const isValidValue = (val) => val !== undefined && val !== null && val !== '';

    // Potential keys in priority order
    const potentialKeys = [AAMCID, SFMatchID, Email, `${FirstName}_${LastName}`];

    // First, try to find a key that exists in uploadStatus
    for (const potentialKey of potentialKeys) {
      if (isValidValue(potentialKey) && uploadStatus[potentialKey]) {
        return potentialKey;
      }
    }

    // If none found in uploadStatus, use first valid value as key
    for (const potentialKey of potentialKeys) {
      if (isValidValue(potentialKey)) {
        return potentialKey;
      }
    }

    // Fallback to name key
    return `${FirstName}_${LastName}`;
  };

  const key = findBestKey();
  const itemUploadStatus =
    uploadStatus[key] && uploadStatus[key].uploadStatus ? uploadStatus[key].uploadStatus : 'queued';

  const nameToDisplay = `${LastName || ''}${LastName && FirstName ? ', ' : ''}${FirstName || ''}`;

  const icon =
    !isFailedToRestore && !isSaving && itemUploadStatus === 'uploaded'
      ? faCheckCircle
      : isSaving
      ? faSpinner
      : itemUploadStatus === 'uploaded' || type === 'orphaned' || type === 'orphaned_conflict'
      ? icons[type]
      : icons[itemUploadStatus];

  let color =
    !isFailedToRestore && !isSaving
      ? colors['updated']
      : type === 'orphaned' || type === 'orphaned_conflict' || itemUploadStatus === 'uploaded'
      ? colors[type]
      : colors[itemUploadStatus];

  let message =
    type === 'orphaned' || type === 'orphaned_conflict' || itemUploadStatus === 'uploaded'
      ? messages[type]
      : messages[itemUploadStatus];

  if (type === 'deleted' && !isFailedToRestore && !isSaving) {
    message = messages['restored'];
    color = colors['updated'];
  }

  const iconID = `icon_${LastName}_${FirstName}_${AAMCID}_${SFMatchID}`.replaceAll(/[ .@]/g, '_');
  const restoreBtnId = `btn_${LastName}_${FirstName}_${AAMCID}_${SFMatchID}`.replaceAll(/[ .@]/g, '_');

  const restoreItem = () => {
    setIsSaving(true);
    getTokenSilently()
      .then((token) => {
        putData(
          'candidate',
          { pk_Department: dContext.department.pk_Department },
          { CandidatePks: [item.pk_Candidate], fieldsToUpdate: { isRemoved: false } },
          formatBearerToken(token),
        )
          .then((results) => {
            // setTimeout(() => {
            setIsSaving(false);
            setIsFailedToRestore(false);
            toast.success(`Successfully restored ${nameToDisplay}`);
            // }, 3000);
          })
          .catch((err) => {
            setIsFailedToRestore(true);
            toast.error(`Failed to restore ${nameToDisplay}`);
            console.log('err: ', err);
            setIsSaving(false);
          });
      })
      .catch((err) => {
        setIsSaving(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  return (
    <div
      key={rowKey}
      id={rowKey}
      style={{
        display: 'flex',
        flexDirection: 'row',
        borderBottom: '1px solid #D0D0D0',
        backgroundColor: color,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: `calc(100% - ${type == 'deleted' ? 90 : 60}px)`,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', padding: 10 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 80,
              marginRight: 10,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {PhotoUrl ? (
              <img src={PhotoUrl} style={{ width: 60, height: 60, borderRadius: 50 }} />
            ) : (
              <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: 50 }} />
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', width: 'calc(100% - 80px)' }}>
            <div style={{ fontSize: 13, fontWeight: 'bold' }}>{nameToDisplay || '---'}</div>
            <div>
              {AAMCID || '---'} {AAMCIDDuplicates.length > 0 && AAMCIDIconWithTooltip}| {SFMatchID || '---'}{' '}
              {SFMatchIDDuplicates.length > 0 && SFMatchIDIconWithTooltip}
            </div>

            <div>
              {Email || '---'} {EmailDuplicates.length > 0 && EmailIconWithTooltip}
            </div>
          </div>
        </div>
        {/* <div style={{ display: 'flex', flexDirection: 'row', padding: 10, fontSize: 13, fontWeight: 'bold' }}>
          {message}
        </div> */}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: type === 'deleted' ? 90 : 60,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <>
          {!isSaving && type === 'deleted' && isFailedToRestore ? (
            <>
              <Button
                size="sm"
                id={restoreBtnId}
                color="primary"
                onClick={() => {
                  if (type === 'deleted') {
                    // UNDO DELETE
                    restoreItem();
                  }
                }}
              >
                {' '}
                Restore{' '}
              </Button>
              {!isSaving && (
                <UncontrolledTooltip placement="top" target={restoreBtnId}>
                  Click to restore deleted candidate.
                </UncontrolledTooltip>
              )}
            </>
          ) : (
            <>
              <FontAwesomeIcon
                id={iconID}
                spin={isSaving || itemUploadStatus === 'uploading'}
                icon={icon}
                style={{
                  fontSize: 25,
                  color: 'black',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (itemUploadStatus == 'failed') {
                    createCandidates([item], true, (err) => {
                      if (err) {
                        toast.error(`Failed to upload ${nameToDisplay}!`);
                      } else {
                        toast.success(`Successfully uploaded ${nameToDisplay}!`);
                      }
                    });
                  }
                }}
              />
              {type == 'orphaned' || type == 'orphaned_conflict' || itemUploadStatus == 'failed' ? (
                <UncontrolledTooltip target={iconID} modifiers={popoverModifiers}>
                  {message}
                  {itemUploadStatus == 'failed' ? (
                    <>
                      <br />
                      Retry uploading this candidate
                    </>
                  ) : null}
                </UncontrolledTooltip>
              ) : null}
              {/* {itemUploadStatus == 'failed' ? (
                <UncontrolledTooltip target={`icon_${LastName}_${FirstName}_${AAMCID}_${SFMatchID}`}>
                  Retry uploading this candidate
                </UncontrolledTooltip>
              ) : null} */}
            </>
          )}
          {/* <FontAwesomeIcon
            id={iconID}
            spin={isSaving || itemUploadStatus === 'uploading'}
            icon={icon}
            style={{
              fontSize: 25,
              color: 'black',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (itemUploadStatus == 'failed') {
                createCandidates([item]);
              }
            }}
          />
          {type == 'orphaned' || type == 'orphaned_conflict' ? (
            <UncontrolledTooltip target={iconID} modifiers={popoverModifiers}>
              {message}
              {itemUploadStatus == 'failed' ? (
                <>
                  <br />
                  Retry uploading this candidate
                </>
              ) : null}
            </UncontrolledTooltip>
          ) : null} */}
          {/* {itemUploadStatus == 'failed' ? (
            <UncontrolledTooltip target={iconID} modifiers={popoverModifiers}>
              Retry uploading this candidate
            </UncontrolledTooltip>
          ) : null} */}
        </>
      </div>
    </div>
  );
};

export default PostImportPreviewItem;
